import { CafeListItemViewModel } from "@dining/api-client";
import { AppProps, StateSlice } from "../..";

export type CafesProps = {
  favoriteCafes: {
    loading: boolean;
    isInitialized: boolean;
    values: CafeListItemViewModel[] | undefined;
    cafeIdsSet: Set<string>;
    error?: Error;
  };
};

export type CafesState = CafesProps & {
  favoriteCafes: {
    setLoading: (loading: boolean) => void;
    setIsInitialized: (isInitialized: boolean) => void;
    setValues: (values: CafeListItemViewModel[]) => void;
    setCafeIdsSet: (cafeIdsSet: Set<string>) => void;
    fetch: () => Promise<void>;
    setError: (error?: Error) => void;
    updateFavoriteCafe: (cafeId: string, isFavorite: boolean) => Promise<void>;
  };
};

export const defaultCafesProps: CafesProps = {
  favoriteCafes: {
    loading: false,
    isInitialized: false,
    values: undefined,
    cafeIdsSet: new Set<string>(),
  },
};

export const createCafesState: (props?: Partial<AppProps>) => StateSlice<CafesState> =
  (props) => (set, get) => ({
    favoriteCafes: {
      ...defaultCafesProps.favoriteCafes,
      ...props?.favoriteCafes,
      setLoading: (loading: boolean) =>
        set(
          (state) => ({
            ...state,
            favoriteCafes: {
              ...state.favoriteCafes,
              loading,
            },
          }),
          false,
          {
            type: "favoriteCafes.setLoading",
            loading,
          },
        ),
      setIsInitialized: (isInitialized: boolean) =>
        set(
          (state) => ({
            ...state,
            favoriteCafes: {
              ...state.favoriteCafes,
              isInitialized,
            },
          }),
          false,
          {
            type: "favoriteCafes.setIsInitialized",
            isInitialized,
          },
        ),
      setError: (error?: Error) =>
        set(
          (state) => ({
            ...state,
            favoriteCafes: {
              ...state.favoriteCafes,
              error,
            },
          }),
          false,
          {
            type: "favoriteCafes.setError",
            error,
          },
        ),
      setValues: (values: CafeListItemViewModel[]) =>
        set(
          (state) => ({
            ...state,
            favoriteCafes: {
              ...state.favoriteCafes,
              values,
            },
          }),
          false,
          {
            type: "favoriteCafes.setValues",
            values,
          },
        ),
      setCafeIdsSet: (cafeIdsSet: Set<string>) =>
        set(
          (state) => ({
            ...state,
            favoriteCafes: {
              ...state.favoriteCafes,
              cafeIdsSet,
            },
          }),
          false,
          {
            type: "favoriteCafes.setCafeIdsSet",
            cafeIdsSet,
          },
        ),
      fetch: async () => {
        const { client, favoriteCafes } = get();
        const { setLoading, setValues, setCafeIdsSet, setError } = favoriteCafes;
        try {
          setLoading(true);
          const response = await client?.getFavoriteCafes();
          if (response?.items) {
            const values = response.items;

            setValues(values);
            //Create a set of cafeids to check if each cafe is favorited in a quick set operation
            const set: Set<string> = new Set<string>();
            values?.forEach((value) => {
              set.add(value?.id);
            });
            setCafeIdsSet(set);
          }
        } catch (error) {
          setError(error as Error);
        } finally {
          setLoading(false);
        }
      },
      updateFavoriteCafe: async (cafeId: string, isFavorite: boolean) => {
        const { client, favoriteCafes } = get();
        const { setError, fetch } = favoriteCafes;

        try {
          if (client) {
            await client.updateFavoriteCafe(cafeId, isFavorite);
            fetch();
          }
        } catch (error) {
          setError(error as Error);
        }
      },
    },
  });
